import * as R from 'ramda';
import * as Yup from 'yup';
import { withFormik } from 'formik';
import React, { useState } from 'react';
import {
  pure,
  branch,
  compose,
  withProps,
  withState,
  lifecycle,
  withHandlers,
  renderNothing,
} from 'react-recompose';
// components
import { FormFooter } from '../../../../components/form-footer';
// features
import { validationSchemaCostsObject } from '../../../work-order/components/costs-section';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
// hocs
import {
  withAsyncConfigs,
  withAsyncSequence,
  withAsyncInvoiceStatusConfigs,
  withAsyncGetServiceVendorListAvailable,
  withAsyncGetEquipmentServiceTemplateList,
  withAsyncGetUserGeneralListFullNameOnDidMount,
} from '../../../../hocs';
// hooks
import { useAiRecognize } from '../../../../hooks';
// ui
import { Flex, scrollableContainerCss4px } from '../../../../ui';
// utilities
import { sendRequest } from '../../../../utilities/http';
import endpointsMap from '../../../../utilities/endpoints';
// feature fleet
import {
  withAddServiceVendor,
  withAsyncGetVehicleTypeByEntityTypeAndEntityGuid,
  withAsyncGetEquipmentServiceByEntityTypeAndServiceGuid,
  withAsyncGetAvailableForServiceEquipmentComponentsByEntityType,
} from '../../equipment-service/hocs';
import { serviceFieldSettings, serviceDefaultFields } from '../../equipment-service/settings/field-settings';
//
import LeftSection from './left-section';
import RightSection from './right-section';
import UpdateRightSection from './update-right-section';
import { withMaintenanceNotes } from '../hooks/use-maintenance-notes';
//////////////////////////////////////////////////

const chargesValidationSchema = Yup.array().of(Yup.object().shape({
  [GC.FIELD_TOTAL]: G.yupNumberRequired,
  [GC.FIELD_DISPLAYED_NAME]: G.yupStringRequired,
}));

const validationSchema = Yup.lazy((values) => {
  const { performedDate, performedValue, serviceVendorType } = values;

  let schema = {
    ...R.map(
      ({ isRequired }) => G.ifElse(isRequired, G.yupStringRequired),
      serviceFieldSettings,
    ),
    [GC.FIELD_PERFORMED_VALUE]: G.yupNumberNotRequired.min(0, G.getShouldBePositiveLocaleTxt()),
    [GC.FIELD_ODOMETER]: G.yupNumberNotRequired.max(999999999, G.getShouldBeLessOrEqualLocaleTxt(999999999)),
    [GC.FIELD_PERFORMED_TYPE]: G.ifElse(
      G.isNotNilAndNotEmpty(performedValue),
      G.yupStringRequired,
      G.yupStringNotRequired,
    ),
  };

  if (G.isNotNilAndNotEmpty(performedDate)) {
    schema = {
      ...schema,
      [GC.FIELD_SERVICE_VENDOR_GUID]: G.yupStringRequired,
      [GC.FIELD_INVOICES]: Yup.array().of(Yup.object()
        .shape(validationSchemaCostsObject)
        .shape({
          [GC.FIELD_CURRENCY]: G.yupStringRequired,
          [GC.FIELD_NET_DAYS]: Yup.number()
            .nullable(true)
            .min(0, G.getShouldBePositiveLocaleTxt())
            .typeError(G.getShouldBeNumericLocaleTxt())
            .max(10000, G.getShouldBeFromToLocaleTxt(0, 10000)),
          [GC.FIELD_INVOICE_NUMBER]: G.ifElse(
            R.equals(serviceVendorType, GC.SERVICE_VENDOR_TYPE_FLEET_SERVICE),
            G.yupStringRequired,
            G.yupStringNotRequired,
          ),
          [GC.FIELD_INVOICE_DATE]: G.ifElse(
            R.equals(serviceVendorType, GC.SERVICE_VENDOR_TYPE_FLEET_SERVICE),
            G.yupStringRequired,
            G.yupStringNotRequired,
          ),
          [GC.FIELD_CHARGES]: chargesValidationSchema,
        })),
    };
  }

  return Yup.object().shape(schema);
});

const setInitialValues = (equipmentService) => {
  if (G.isNilOrEmpty(equipmentService)) return null;

  const { invoice } = equipmentService;

  const serviceType = R.pathOr(null, [GC.FIELD_SERVICE_TYPE, GC.FIELD_DROPDOWN_OPTION_GUID], equipmentService);

  if (G.isNilOrEmpty(invoice)) {
    return R.assoc(GC.FIELD_SERVICE_TYPE, serviceType, equipmentService);
  }

  const status = R.pathOr(null, [GC.SYSTEM_OBJECT_INVOICE, GC.FIELD_STATUS, GC.FIELD_CONFIG_GUID], equipmentService);

  const glCode = R.pathOr(
    null,
    [GC.SYSTEM_OBJECT_INVOICE, GC.FIELD_GL_CODE, GC.FIELD_DROPDOWN_OPTION_GUID],
    equipmentService,
  );

  return {
    ...equipmentService,
    serviceType,
    serviceVendorType: R.pathOr(null, [GC.SYSTEM_OBJECT_SERVICE_VENDOR, GC.FIELD_TYPE], equipmentService),
    [GC.FIELD_INVOICES]: [
      {
        ...invoice,
        status,
        glCode,
      },
    ],
  };
};

const serviceVendorTypes = R.join(
  ',',
  [GC.SERVICE_VENDOR_TYPE_FLEET_SERVICE, GC.SERVICE_VENDOR_TYPE_FLEET_SELF_SERVICE],
);

const makeFilesFormDataAndDocumentTypes = (data) => {
  if (G.isNilOrEmpty(data)) return { files: [], documentTypes: [] };

  const filteredData = R.reject(({ file, documentType }) =>
    R.or(G.isNilOrEmpty(file), G.isNilOrEmpty(documentType)), data);

  const files = [];

  const documentTypes = [];

  if (G.isNotNilAndNotEmpty(filteredData)) {
    filteredData.forEach(({ file, documentType }) => {
      files.push(file);

      documentTypes.push(documentType);
    });
  }

  return { files, documentTypes };
};

const enhance = compose(
  withState('aiUploadDoc', 'setAiUploadDoc', null),
  withState('leftActiveTab', 'setLeftActiveTab', 0),
  withState('maintenanceDocs', 'setMaintenanceDocs', null),
  withState('currentUploadedDoc', 'setCurrentUploadedDoc', null),
  withProps(() => ({
    type: 'fleetService',
    configsNamesArray: [
      GC.INVOICE_GL_CODE,
      GC.FLEET_EQUIPMENT_SERVICE_TYPE,
      GC.GENERAL_BRANCH_DEFAULT_CURRENCY,
      GC.SERVICE_VENDOR_VENDOR_SERVICE_TYPE,
      GC.TRUCK_MAINTENANCE_AI_UPLOAD_DEFAULT_DOCUMENT_TYPE,
      GC.TRUCK_MAINTENANCE_DOCUMENTS_DEFAULT_DOCUMENT_TYPE,
      GC.TRAILER_MAINTENANCE_AI_UPLOAD_DEFAULT_DOCUMENT_TYPE,
      GC.TRAILER_MAINTENANCE_DOCUMENTS_DEFAULT_DOCUMENT_TYPE,
    ],
  })),
  withAsyncConfigs,
  withAsyncInvoiceStatusConfigs,
  withAsyncGetEquipmentServiceTemplateList,
  withAsyncGetUserGeneralListFullNameOnDidMount,
  withAsyncGetServiceVendorListAvailable(serviceVendorTypes),
  withAsyncGetAvailableForServiceEquipmentComponentsByEntityType,
  withFormik({
    validationSchema,
    enableReinitialize: true,
    mapPropsToValues: ({ isCreateMode, initialValues, asyncSequence, equipmentService }) => {
      const defaultFields = G.isNotNilAndNotEmpty(asyncSequence) ?
        {...serviceDefaultFields, [GC.FIELD_SERVICE_ID]: asyncSequence, [GC.SYSTEM_OBJECT_INVOICE]: null } :
        R.assoc(GC.SYSTEM_OBJECT_INVOICE, null, serviceDefaultFields);

      return G.setInitialFormikValues(defaultFields, initialValues, setInitialValues(equipmentService, isCreateMode));
    },
    handleSubmit: (values, { props }) => {
      const {
        isEditMode,
        submitAction,
        asyncSequence,
        maintenanceDocs,
        maintenanceNotes,
      } = props;

      let valuesToUse = G.mapObjectEmptyStringFieldsToNull(values);

      if (R.pathEq(asyncSequence, [GC.FIELD_SERVICE_ID], values)) {
        valuesToUse = R.assoc(GC.FIELD_SERVICE_ID, null, valuesToUse);
      }

      if (R.and(R.not(isEditMode), G.isNotNilAndNotEmpty(maintenanceNotes))) {
        valuesToUse = R.assoc('notes', maintenanceNotes, valuesToUse);
      }

      if (G.isNotNilAndNotEmpty(maintenanceDocs)) {
        const { files, documentTypes } = makeFilesFormDataAndDocumentTypes(maintenanceDocs);

        valuesToUse = { ...valuesToUse, files, documentTypes };
      }

      submitAction(valuesToUse);
    },
  }),
  withHandlers({
    getDocumentList: (props) => async () => {
      const { entityType, setDocuments, openLoader, closeLoader, primaryObjectGuid } = props;

      openLoader();

      const endpoint = R.equals(entityType, 'trailer') ?
        endpointsMap.trailerEquipmentServiceDocumentList :
        endpointsMap.truckEquipmentServiceDocumentList;

      const res = await sendRequest('get', endpoint, { params: { primaryObjectGuid } });

      const { data, status } = res;

      if (G.isResponseFail(status)) {
        G.handleFailResponseSimple(res, 'AiUpload -> getDocumentList fail');
      } else {
        setDocuments(data);
      }

      closeLoader();
    },
    updateMaintenanceDoc: (props) => (file) => {
      const {
        aiUploadDoc,
        setDocuments,
        setAiUploadDoc,
        maintenanceDocs,
      } = props;

      const preview = G.createObjectURL(file);

      const doc = {
        ...aiUploadDoc,
        file,
        preview,
      };

      const guid = G.getGuidFromObject(aiUploadDoc);

      setAiUploadDoc(doc);

      const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), maintenanceDocs);

      if (R.gte(index, 0)) {
        const documentsToUpdate = R.clone(maintenanceDocs);

        documentsToUpdate[index] = doc;

        setDocuments(documentsToUpdate);
      } else {
        setDocuments(R.prepend(doc, maintenanceDocs));
      }
    },
  }),
  withHandlers({
    updateDocument: (props) => async (file, document) => {
      const {
        entityType,
        openLoader,
        closeLoader,
        getDocumentList,
        currentUploadedDoc,
        updateMaintenanceDoc,
        setCurrentUploadedDoc,
      } = props;

      openLoader();

      if (G.isNilOrEmpty(document)) updateMaintenanceDoc(file);

      const documentToUpdate = G.ifElse(
        G.isNotNilAndNotEmpty(document),
        document,
        currentUploadedDoc,
      );

      const reqFormData = G.makeDataForDocument({
        ...documentToUpdate,
        file,
      });

      const endpoint = R.equals(entityType, 'trailer') ?
        endpointsMap.trailerEquipmentServiceDocumentUpdate :
        endpointsMap.truckEquipmentServiceDocumentUpdate;

      const res = await sendRequest('post', endpoint, { data: reqFormData });

      const { data, status } = res;

      if (G.isResponseFail(status)) {
        G.handleFailResponseSimple(res, 'AiUpload -> addDocument fail');
      } else {
        if (G.isNilOrEmpty(document)) setCurrentUploadedDoc(data);

        getDocumentList();
      }

      closeLoader();
    },
  }),
  withAddServiceVendor,
  withProps((props) => {
    const { entityType, asyncConfigs } = props;

    const invoiceStatusOptions = G.mapCustomConfigOptionsFromProps('FleetServiceIStatusConfig', props, true);

    const glCodeOptions = G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
      asyncConfigs,
      GC.INVOICE_GL_CODE,
      true,
    );

    const equipmentServiceTypeOptions = G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
      asyncConfigs,
      GC.FLEET_EQUIPMENT_SERVICE_TYPE,
      true,
    );

    const truckAiUploadDefaultDocumentType = G.getConfigValueFromStore(
      GC.TRUCK_MAINTENANCE_AI_UPLOAD_DEFAULT_DOCUMENT_TYPE,
      asyncConfigs,
    );

    const truckDocumentsDefaultDocumentType = G.getConfigValueFromStore(
      GC.TRUCK_MAINTENANCE_DOCUMENTS_DEFAULT_DOCUMENT_TYPE,
      asyncConfigs,
    );

    const trailerAiUploadDefaultDocumentType = G.getConfigValueFromStore(
      GC.TRAILER_MAINTENANCE_AI_UPLOAD_DEFAULT_DOCUMENT_TYPE,
      asyncConfigs,
    );

    const trailerDocumentsDefaultDocumentType = G.getConfigValueFromStore(
      GC.TRAILER_MAINTENANCE_DOCUMENTS_DEFAULT_DOCUMENT_TYPE,
      asyncConfigs,
    );

    const aiUploadDefaultDocumentType = R.equals(entityType, 'trailer') ?
      trailerAiUploadDefaultDocumentType : truckAiUploadDefaultDocumentType;

    const documentsDefaultDocumentType = R.equals(entityType, 'trailer') ?
      trailerDocumentsDefaultDocumentType : truckDocumentsDefaultDocumentType;

    return {
      glCodeOptions,
      invoiceStatusOptions,
      equipmentServiceTypeOptions,
      aiUploadDefaultDocumentType,
      documentsDefaultDocumentType,
    };
  }),
  lifecycle({
    componentDidUpdate(prevProps) {
      const props = this.props;

      const {
        setFieldValue,
        serviceVendorListAvailableOptions,
      } = props;

      const getServiceVendorGuid = R.path(['values', GC.FIELD_SERVICE_VENDOR_GUID]);

      const serviceVendorGuid = getServiceVendorGuid(props);

      if (G.notEquals(getServiceVendorGuid(props), getServiceVendorGuid(prevProps))) {
        if (G.isNotNilAndNotEmpty(serviceVendorListAvailableOptions)) {
          const serviceVendorType = R.compose(
            R.pathOr(null, [GC.FIELD_TYPE]),
            R.find(R.propEq(serviceVendorGuid, GC.FIELD_VALUE)),
          )(serviceVendorListAvailableOptions);

          setFieldValue('serviceVendorType', serviceVendorType);
        }
      }
    },
  }),
  pure,
);

const MaintenanceForm = (props) => {
  const {
    isEditMode,
    handleSubmit,
    leftActiveTab,
    setLeftActiveTab,
    equipmentServiceTemplateList,
  } = props;

  const { aiRecognizeRequest, aiRecognizeLoading } = useAiRecognize({ type: 'MAINTENANCE' });

  const [rightActiveTab, setRightActiveTab] = useState(0);

  const RightSectionComponent = G.isTrue(isEditMode) ? UpdateRightSection : RightSection;

  return (
    <form onSubmit={handleSubmit}>
      <Flex
        overflow='auto'
        alignItems='start'
        height='calc(100vh - 60px)'
        css={scrollableContainerCss4px}
      >
        <LeftSection
          {...props}
          leftActiveTab={leftActiveTab}
          setLeftActiveTab={setLeftActiveTab}
          aiRecognizeLoading={aiRecognizeLoading}
          templates={equipmentServiceTemplateList}
        />
        <RightSectionComponent
          {...props}
          rightActiveTab={rightActiveTab}
          setRightActiveTab={setRightActiveTab}
          aiRecognizeRequest={aiRecognizeRequest}
        />
      </Flex>
      <FormFooter
        submitDisabled={aiRecognizeLoading}
        boxStyles={{
          p: 15,
          borderTop: '1px solid',
          bg: G.getTheme('colors.whiteGrey'),
          borderColor: G.getTheme('colors.lightGrey'),
        }}
      />
    </form>
  );
};

const enhanceCreate = compose(
  withMaintenanceNotes({}),
  withProps({
    isCreateMode: true,
    sequenceConfigName: GC.FLEET_GENERAL_EQUIPMENT_SERVICE_ID_SEQUENCE,
    autogenerateConfigName: GC.FLEET_GENERAL_EQUIPMENT_SERVICE_ID_AUTOGENERATED,
    configsNamesArray:
      [GC.FLEET_GENERAL_EQUIPMENT_SERVICE_ID_SEQUENCE, GC.FLEET_GENERAL_EQUIPMENT_SERVICE_ID_AUTOGENERATED],
  }),
  withAsyncSequence,
  enhance,
);

const enhanceEdit = compose(
  withProps({ isEditMode: true }),
  withState('documents', 'setDocuments', null),
  withState('maintenanceNotes', 'setMaintenanceNotes', null),
  withState('localItemList', 'setLocalItemList', R.prop('documentList')),
  withAsyncGetVehicleTypeByEntityTypeAndEntityGuid,
  withAsyncGetEquipmentServiceByEntityTypeAndServiceGuid,
  branch(
    ({ isEquipmentServiceLoaded }) => isEquipmentServiceLoaded,
    enhance,
    renderNothing,
  ),
);

export const EditMaintenanceForm = enhanceEdit(MaintenanceForm);

export default enhanceCreate(MaintenanceForm);
